import { createElement, setStyleAttribute } from '@syncfusion/ej2-base';
import { tooltipRender } from '../model/constant';
import { Tooltip } from '@syncfusion/ej2-popups';
import { getElement, getLabelFormat, getTemplateFunction, GaugeLocation, Size } from '../utils/helper';
import { getPointer, convertPixelToValue } from '../utils/helper';
/**
 * Represent the tooltip rendering for gauge
 */
var GaugeTooltip = /** @class */ (function () {
    function GaugeTooltip(gauge) {
        this.gauge = gauge;
        this.element = gauge.element;
        this.tooltip = gauge.tooltip;
        this.textStyle = this.tooltip.textStyle;
        this.borderStyle = this.tooltip.border;
        this.ejTooltip = new Tooltip({
            opensOn: 'custom',
            beforeRender: this.onBeforeRender.bind(this),
            beforeOpen: this.tooltipCustomization.bind(this),
            openDelay: 0, closeDelay: 1000
        });
        this.ejTooltip.appendTo(this.element);
    }
    /**
     * Internal use for tooltip rendering
     * @param pointerElement
     */
    GaugeTooltip.prototype.renderTooltip = function (pointerElement) {
        this.pointerElement = pointerElement;
        var clientRect = this.gauge.element.getBoundingClientRect();
        var current = getPointer(pointerElement, this.gauge);
        this.currentAxis = current.axis;
        this.axisIndex = current.axisIndex;
        this.currentPointer = current.pointer;
        var ele = this.appendTargetElement();
        if (pointerElement.getAttribute('data-tooltip-id') === null && ele.getAttribute('aria-describedby') === null) {
            this.ejTooltip.open(ele);
        }
    };
    GaugeTooltip.prototype.appendTargetElement = function () {
        var location = this.getTooltipLocation();
        var element = getElement(this.element.id + '_EJTooltip_');
        var elementSpace = 2;
        if (element) {
            element.style.left = (location.x - (elementSpace / 2)) + 'px';
            element.style.top = (location.y - (elementSpace / 2)) + 'px';
        }
        else {
            element = createElement('div', {
                id: this.element.id + '_EJTooltip_',
                styles: 'position:absolute; left:' + location.x + 'px;top:' + location.y + 'px;'
            });
            element.appendChild(createElement('div', {
                id: this.element.id + '_GaugeTooltip',
                styles: 'position:absolute;width:' + elementSpace + 'px;height:' + elementSpace + 'px;background:transparent'
            }));
            getElement(this.element.id + '_Secondary_Element').appendChild(element);
        }
        return element;
    };
    GaugeTooltip.prototype.getTooltipPosition = function () {
        var position;
        if (this.gauge.orientation === 'Vertical') {
            position = (!this.currentAxis.opposedPosition) ? 'LeftCenter' : 'RightCenter';
        }
        else {
            position = (this.currentAxis.opposedPosition) ? 'TopCenter' : 'BottomCenter';
        }
        return position;
    };
    GaugeTooltip.prototype.getTooltipLocation = function () {
        var location;
        var bounds;
        var radix = 10;
        var lineX;
        var lineY;
        var size = new Size(this.gauge.availableSize.width, this.gauge.availableSize.height);
        var x;
        var y;
        var height;
        var width;
        var lineId = this.gauge.element.id + '_AxisLine_' + this.axisIndex;
        var tickID = this.gauge.element.id + '_MajorTicksLine_' + this.axisIndex;
        var lineBounds;
        if (getElement(lineId)) {
            lineBounds = getElement(lineId).getBoundingClientRect();
            lineX = lineBounds.left;
            lineY = lineBounds.top;
        }
        else {
            lineBounds = getElement(tickID).getBoundingClientRect();
            lineX = (!this.currentAxis.opposedPosition) ? (lineBounds.left + lineBounds.width) : lineBounds.left;
            lineY = (!this.currentAxis.opposedPosition) ? (lineBounds.top + lineBounds.height) : lineBounds.top;
        }
        bounds = this.pointerElement.getBoundingClientRect();
        var elementRect = this.gauge.element.getBoundingClientRect();
        x = bounds.left - elementRect.left;
        y = bounds.top - elementRect.top;
        height = bounds.height;
        width = bounds.width;
        if (this.gauge.orientation === 'Vertical') {
            x = (lineX - elementRect.left);
            y = (this.currentPointer.type === 'Marker') ? y + (height / 2) : (!this.currentAxis.isInversed) ? y : y + height;
        }
        else {
            y = (lineY - elementRect.top);
            x = (this.currentPointer.type === 'Marker') ? (x + width / 2) : (!this.currentAxis.isInversed) ? x + width : x;
        }
        location = new GaugeLocation(x, y);
        return location;
    };
    GaugeTooltip.prototype.onBeforeRender = function (args) {
        var pointerValue = convertPixelToValue(this.element, this.pointerElement, this.gauge.orientation, this.currentAxis, 'tooltip', null);
        var tooltipFormat = this.gauge.tooltip.format || this.currentAxis.labelStyle.format;
        var customLabelFormat = tooltipFormat && tooltipFormat.match('{value}') !== null;
        var format = this.gauge.intl.getNumberFormat({
            format: getLabelFormat(tooltipFormat), useGrouping: this.gauge.useGroupingSeparator
        });
        var content = customLabelFormat ?
            tooltipFormat.replace(new RegExp('{value}', 'g'), format(pointerValue)) :
            format(pointerValue);
        content = this.tooltip.template ? getTemplateFunction(this.tooltip.template)({ 'value': content })[0] : content;
        var argsData = {
            cancel: false, name: tooltipRender,
            content: content,
            border: this.borderStyle,
            axis: this.currentAxis, pointer: this.currentPointer,
            textStyle: this.textStyle
        };
        this.gauge.trigger(tooltipRender, argsData);
        this.ejTooltip.content = argsData.content;
        this.textStyle = argsData.textStyle;
        this.borderStyle = argsData.border;
        this.ejTooltip.position = this.getTooltipPosition();
        this.ejTooltip.dataBind();
    };
    GaugeTooltip.prototype.tooltipCustomization = function (args) {
        var font = this.textStyle;
        var borderColor = this.borderStyle.color;
        var border = this.borderStyle.width;
        var pointerSize = 8;
        var arrowWidth = (pointerSize - border) + 'px';
        var outerWidth;
        var innerWidth;
        args.element.classList.remove('e-popup-close');
        args.element.classList.add('e-popup-open');
        var arrowEle = args.element.querySelector('.e-arrow-tip');
        setStyleAttribute(args.element, {
            'backgroundColor': this.tooltip.fill, 'borderColor': borderColor || '#212121',
            'borderWidth': border + 'px', 'pointer-events': 'none', 'borderRadius': '5px', 'padding-left': '5px',
            'padding-right': '5px', 'padding-top': '3px', 'padding-bottom': '3px'
        });
        setStyleAttribute(args.element.querySelector('.e-tip-content'), {
            'color': font.color, 'fontFamily': font.fontFamily, 'fontSize': font.size,
            'fontWeight': font.fontWeight, 'opacity': font.opacity.toString(), 'fontStyle': font.fontStyle
        });
        setStyleAttribute(args.element.querySelector('.e-arrow-tip'), {
            'width': 2 * (pointerSize + border) + 'px', 'height': pointerSize + border + 'px'
        });
        if (arrowEle.classList.contains('e-tip-top')) {
            pointerSize = args.element.querySelector('.e-arrow-tip').offsetHeight;
            outerWidth = pointerSize + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-outer'), {
                'borderRightColor': 'transparent', 'borderLeftColor': 'transparent', 'borderBottomColor': this.tooltip.border.width > 0 ?
                    this.tooltip.border.color : 'transparent', 'borderLeftWidth': outerWidth, 'borderRightWidth': outerWidth,
                'borderBottomWidth': outerWidth,
            });
            innerWidth = (pointerSize - border) + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-inner'), {
                'borderRightColor': 'transparent', 'borderLeftColor': 'transparent', 'borderBottomColor': 'transparent',
                'borderLeftWidth': innerWidth, 'borderRightWidth': innerWidth, 'borderBottomWidth': innerWidth,
                'left': border + 'px', 'top': border + 'px'
            });
        }
        else if (arrowEle.classList.contains('e-tip-bottom')) {
            pointerSize = args.element.querySelector('.e-arrow-tip').offsetHeight;
            outerWidth = pointerSize + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-outer'), {
                'borderRightColor': 'transparent', 'borderLeftColor': 'transparent', 'borderTopColor': this.tooltip.border.width > 0 ?
                    this.tooltip.border.color : 'transparent', 'borderLeftWidth': outerWidth, 'borderRightWidth': outerWidth,
                'borderTopWidth': outerWidth,
            });
            innerWidth = (pointerSize - border) + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-inner'), {
                'borderRightColor': 'transparent', 'borderLeftColor': 'transparent', 'borderTopColor': 'transparent',
                'borderLeftWidth': innerWidth, 'borderRightWidth': innerWidth, 'borderTopWidth': innerWidth,
                'left': border + 'px', 'top': '0'
            });
        }
        else if (arrowEle.classList.contains('e-tip-left')) {
            setStyleAttribute(args.element.querySelector('.e-arrow-tip'), {
                'width': pointerSize + border + 'px', 'height': 2 * (pointerSize + border) + 'px'
            });
            pointerSize = args.element.querySelector('.e-arrow-tip').offsetWidth;
            outerWidth = pointerSize + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-outer'), {
                'borderTopColor': 'transparent', 'borderBottomColor': 'transparent', 'borderRightColor': borderColor,
                'borderTopWidth': outerWidth, 'borderBottomWidth': outerWidth, 'borderRightWidth': outerWidth
            });
            innerWidth = (pointerSize - border) + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-inner'), {
                'borderTopColor': 'transparent', 'borderBottomColor': 'transparent', 'borderRightColor': this.tooltip.fill,
                'borderTopWidth': innerWidth, 'borderBottomWidth': innerWidth, 'borderRightWidth': innerWidth,
                'left': border + 'px', 'top': border + 'px'
            });
        }
        else {
            setStyleAttribute(args.element.querySelector('.e-arrow-tip'), {
                'width': (pointerSize + border) + 'px', 'height': 2 * (pointerSize + border) + 'px'
            });
            pointerSize = args.element.querySelector('.e-arrow-tip').offsetWidth;
            outerWidth = pointerSize + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-outer'), {
                'borderTopColor': 'transparent', 'borderBottomColor': 'transparent', 'borderLeftColor': borderColor,
                'borderTopWidth': outerWidth, 'borderBottomWidth': outerWidth, 'borderLeftWidth': outerWidth
            });
            innerWidth = (pointerSize - border) + 'px';
            setStyleAttribute(args.element.querySelector('.e-arrow-tip-inner'), {
                'borderTopColor': 'transparent', 'borderBottomColor': 'transparent', 'borderLeftColor': this.tooltip.fill,
                'borderTopWidth': innerWidth, 'borderBottomWidth': innerWidth, 'borderLeftWidth': innerWidth,
                'left': (border <= 1 ? 0 : -1) + 'px', 'top': border + 'px'
            });
        }
        this.ejTooltip.refresh(args.target);
        setStyleAttribute(args.element, {
            'display': 'block', 'transitionProperty': 'left,top',
            'transitionDuration': this.tooltip.enableAnimation ? '1000ms' : '0ms'
        });
    };
    /*
     * Get module name.
     */
    GaugeTooltip.prototype.getModuleName = function () {
        return 'Tooltip';
    };
    /**
     * To destroy the tooltip.
     * @return {void}
     * @private
     */
    GaugeTooltip.prototype.destroy = function (gauge) {
        // Destroy method performed here
    };
    return GaugeTooltip;
}());
export { GaugeTooltip };
